/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState } from "react";
import { Info } from "lucide-react";
import { SCORE_MAX, SCORE_MIN, SIGNALS, type DuplicateSignal } from "../../api/duplicates";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";

/**
 * The score with its explanation one click away. The modal states what a signal is, what each
 * one means precisely, and that a match is evidence, not proof — the review decides.
 */
export function ScoreBadge({
  score,
  signals,
  size = "sm",
}: {
  score: number;
  signals: DuplicateSignal[];
  size?: "sm" | "md";
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        className="relative z-10 inline-flex items-center gap-1 rounded-full transition-opacity hover:opacity-80"
        aria-label={`Match score ${score}. How scoring works`}
      >
        <Badge tone="warning">
          Score {score}
          <Info size={size === "md" ? 12 : 10} className="ml-1 inline-block" />
        </Badge>
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="How scoring works" size="md">
        <div className="space-y-5 px-6 py-5 text-sm leading-relaxed text-text-secondary">
          <p>
            A signal is a recorded fact that makes two accounts look like the same person. Each
            signal carries a weight, and the score is the sum of every signal that fired. With
            today's signals, scores range from {SCORE_MIN} to {SCORE_MAX}; higher means more
            corroborating facts.
          </p>

          <ol className="space-y-3">
            {(Object.entries(SIGNALS) as [DuplicateSignal, (typeof SIGNALS)[DuplicateSignal]][]).map(
              ([key, info]) => {
                const fired = signals.includes(key);
                return (
                  <li
                    key={key}
                    className={
                      fired
                        ? "rounded-2xl border border-warning/40 bg-warning/5 p-4"
                        : "rounded-2xl border border-border p-4"
                    }
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-text-primary">{info.label}</span>
                      <span className="shrink-0 text-xs text-text-tertiary">
                        Weight {info.weight}
                        {fired && " · fired on this case"}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed">{info.description}</p>
                  </li>
                );
              },
            )}
          </ol>

          <p className="text-xs text-text-tertiary">
            A match is evidence, not proof. Twins, shared family phones, and coincidences exist;
            deciding whether these are the same person is exactly what this review is for.
          </p>

          <p className="font-medium text-text-primary">
            This case scored {score} of {SCORE_MAX}.
          </p>
        </div>
      </Modal>
    </>
  );
}
